import { commits, dispatches, getters } from '@/modules/core/app/helpers/store';
import { OnboardingFlow, OnboardingStep } from '@/modules/core/onboarding/onboarding.constants';
import OnboardingResource from '@/modules/core/onboarding/resources/OnboardingResource';
import { PlatformSource } from '@/modules/core/session/session.constants';

export class OnboardingService {
  /**
   * @returns {Promise<{state: object, preferred_flow: string, completed_at: ?string}>}
   */
  async getFlowState() {
    return OnboardingResource.getFlowState();
  }

  /**
   * @param {object} state
   * @param {string} preferredFlow
   * @returns {Promise<object>}
   */
  async saveFlowState(state, preferredFlow = OnboardingFlow.AUTOPILOT) {
    return OnboardingResource.saveFlowState(state, preferredFlow);
  }

  /**
   * @param {string} preferredFlow
   * @returns {Promise<object>}
   */
  async savePreferredFlow(preferredFlow = OnboardingFlow.AUTOPILOT) {
    return OnboardingResource.savePreferredFlow(preferredFlow);
  }

  /**
   * @param {object} params
   * @returns {Promise<{queued: boolean, build_token: string, reason: ?string}>}
   */
  async generateDashboard(params) {
    return OnboardingResource.generateDashboard(params);
  }

  /**
   * @param {string} buildToken identifies the build started from the current answers
   * @returns {Promise<{status: string, page_id: number, reason: string, build_token: string}>}
   */
  async getDashboardBuildStatus(buildToken) {
    return OnboardingResource.getDashboardBuildStatus(buildToken);
  }

  /**
   * @param userSettings
   * @returns {boolean}
   */
  isAutopilotFlow(userSettings = getters.session.getUserSettings()) {
    return Boolean(userSettings.isAutopilotOnboardingFlow);
  }

  /**
   * @param userSettings
   * @returns {boolean}
   */
  isSmartReportsTrial(userSettings = getters.session.getUserSettings()) {
    return Boolean(
      userSettings.isFreeTrial && userSettings.platformSource === PlatformSource.SMARTREPORTS
    );
  }

  /**
   * The router sends `/onboarding` to the classic page when this is true.
   *
   * @param userSettings
   * @returns {boolean}
   */
  shouldUseClassicOnboarding(userSettings = getters.session.getUserSettings()) {
    return !this.isAutopilotFlow(userSettings) && !this.isSmartReportsTrial(userSettings);
  }

  /**
   * Records the preference and refreshes the nav; the caller routes afterwards.
   *
   * @returns {Promise<void>}
   */
  async switchToAutopilotFlow() {
    try {
      await this.savePreferredFlow(OnboardingFlow.AUTOPILOT);
    } catch (e) {
      // Non-blocking: still switch the UI even if persistence fails.
    }
    commits.session.setPreferredOnboardingFlow(OnboardingFlow.AUTOPILOT);
    await dispatches.nav.refreshAppNavItems().catch(() => {});
  }

  /**
   * The flows have different finish lines; isOnboarding suits neither, as it flips
   * false as soon as a data source exists -- which both flows do mid-flight.
   *
   * @param userSettings
   * @returns {boolean}
   */
  isOnboardingFinished(userSettings = getters.session.getUserSettings()) {
    return this.isAutopilotFlow(userSettings)
      ? Boolean(userSettings.isOnboardingCompleted)
      : !userSettings.showOnboardingMenu;
  }

  /**
   * @param item
   * @returns {*|boolean}
   */
  hasOnboardingStepCompleted(item) {
    return (
      item.isOnboradingItem() &&
      getters.session.getUserSettings().completedOnboardingSteps.includes(item.key)
    );
  }

  /**
   * @param item
   * @returns {string|*}
   */
  getOnboardingStepIcon(item) {
    if (getters.session.getUserSettings().completedOnboardingSteps.includes(item.key)) {
      return 'check-circle';
    }

    return item.icon;
  }

  /**
   * @param item
   * @returns {boolean}
   */
  isOnboardingNavItemDisabled(item) {
    if (!item.isOnboradingItem() || item.key === OnboardingStep.CONNECT_DATA) {
      return false;
    }

    return !getters.session
      .getUserSettings()
      .completedOnboardingSteps.includes(OnboardingStep.CONNECT_DATA);
  }
}

export default new OnboardingService();
